import { FontFamilyListBoxProps } from './Types'
import { fontWeight } from 'screens/BrandStandard/CustomFontsLoader'
import { Item, Section } from '@react-stately/collections'
import Styles from './FontFamilyPanel.module.scss'
import { CollectionElement, Selection } from '@react-types/shared'
import { Key, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { ListBoxProps } from 'components/ListBox/Types'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { useListState } from '@react-stately/list'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useListBoxLayout } from 'components/ListBox/useListBoxLayout'
import cx from 'classnames'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'

interface FontListBoxProps extends ListBoxProps<any> {
  ref: React.RefObject<HTMLDivElement>
  inline?: boolean
}

const FontListBox = ({ ref, inline, ...props }: FontListBoxProps) => {
  const state = useListState({
    ...props,
    selectionMode: 'single',
    selectionBehavior: 'replace',
  })
  const layout = useListBoxLayout(state, false)

  return (
    <ListBoxBase
      wrapperClassName={cx(Styles.listWrapper, { [Styles.inline]: inline })}
      optionClassName={Styles.option}
      state={state}
      isVirtualized
      layout={layout}
    />
  )
}

const FontFamilyListBox = observer(
  ({ selectedFont, onSelectionChange, fonts = [], filter = '', inline }: FontFamilyListBoxProps) => {
    const { fonts: fontsStore } = useRootStore()
    const ref = useRef<HTMLDivElement>(null)
    const filteredFonts = filter
      ? fonts.filter((font) => font.font_family.toLowerCase().includes(filter.toLowerCase()))
      : fonts
    const filteredBlazeFonts = filter
      ? fontsStore.list(['blaze', 'google']).filter((font) => font.family.toLowerCase().includes(filter.toLowerCase()))
      : fontsStore.list(['blaze', 'google'])

    if (filteredFonts.length === 0 && filteredBlazeFonts.length === 0) {
      return <Heading level={5}>{`No fonts were found for "${filter}"`}</Heading>
    }

    return (
      <FontListBox
        ref={ref}
        inline={inline}
        selectedKeys={new Set([selectedFont])}
        onSelectionChange={(keys: Selection) => {
          const key = (keys as Set<Key>).values().next().value

          const newFamily = key as string
          if (!newFamily) return

          const customFont = fonts.find((font) => font.font_family === newFamily)
          const customFontWeights: Array<FontWeightType> = customFont ? [fontWeight(customFont)] : [400]

          const weights = fontsStore.get(newFamily)?.weights || customFontWeights

          onSelectionChange(newFamily, weights)
        }}
      >
        {
          [
            filteredFonts.length > 0 && (
              <Section key="your fonts" title="Your Fonts">
                {filteredFonts.map(({ font_family }) => (
                  <Item key={font_family} textValue={font_family}>
                    <span className={Styles.fontFamily} style={{ fontFamily: font_family, fontSize: 30 }}>
                      {font_family}
                    </span>
                  </Item>
                ))}
              </Section>
            ),
            filteredBlazeFonts.length > 0 && (
              <Section key="google fonts" title="Google Fonts">
                {filteredBlazeFonts.map((font) => {
                  const isLoaded = fontsStore.get(font.family)?.loadingState === 'loaded'

                  return (
                    <Item key={font.family} textValue={font.family}>
                      <span
                        style={{ fontFamily: font.family, fontSize: 30, color: !isLoaded ? 'transparent' : undefined }}
                        className={Styles.fontFamily}
                      >
                        {font.family} {!isLoaded && <LineSkeleton className={Styles.fontFamilyShimmer} />}
                      </span>
                      <GoogleFontLoader family={font.family} root={ref.current} />
                      <BlazeFontLoader name={font.family} root={ref.current} />
                    </Item>
                  )
                })}
              </Section>
            ),
          ].filter(Boolean) as CollectionElement<HTMLElement>[]
        }
      </FontListBox>
    )
  },
)

const FontFamilyListBoxContainer = (props: Omit<FontFamilyListBoxProps, 'fonts'>) => {
  const { data } = useFonts({ cacheTime: 1000 * 60 * 5 })

  return <FontFamilyListBox fonts={data?.fonts} {...props} />
}

export { FontFamilyListBoxContainer as FontFamilyListBox }
